// Creator Studio per-stream control room (YouTube-Studio style). Replaces the old slide-over
// drawer with a full page at /studio/:uuid: a live preview + real-time chat at the top ("focus"
// row), then all the stream's info below — push/ingest address ("推流地址"), editable details,
// stream-key reset, recordings, and the destructive end-stream.
//
// The socket here drives the preview's live status/viewers and feeds the ChatPanel (a single
// shared connection). The body is keyed by uuid so switching streams resets form state.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Eye,
  KeyRound,
  Server,
  Square,
  Video,
  type LucideIcon,
} from "lucide-react";
import { api } from "../api/client.js";
import {
  useEndStream,
  useMe,
  useRegenerateKey,
  useStream,
  useStreamIngest,
  useUpdateStream,
  type UpdateStreamBody,
} from "../api/hooks.js";
import { hasPublish, type MeDTO, type StreamDetailDTO } from "../api/types.js";
import { useLoginModal } from "../ui/loginModal.js";
import { useStreamSocket } from "../ws/useStreamSocket.js";
import { Player } from "../player/Player.js";
import { ChatPanel } from "../components/ChatPanel.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { LiveBadge, PausedBadge, StatusBadge } from "../components/ui/LiveBadge.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { formatCount, formatUptime, relativeTime } from "../components/ui/format.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog.js";
import { pageVariants } from "@/lib/motion";

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const textareaCls =
  "w-full resize-none rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-foreground shadow-inner shadow-black/20 outline-none transition placeholder:text-muted-foreground/90 focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/40";

function CopyField({ label, value }: { label?: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-foreground">
          {value}
        </code>
        <Button variant="secondary" size="sm" onClick={copy} className="shrink-0">
          {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  mono,
}: {
  label: string;
  value: string;
  icon?: LucideIcon;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-white/[0.06] bg-card/50 px-3.5 py-2.5">
      <p className="mb-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p
        className={`flex items-center gap-1.5 truncate text-sm font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
        {value}
      </p>
    </div>
  );
}

// ── Ingest ("推流地址") card ─────────────────────────────────────────────
function IngestSection({ uuid }: { uuid: string }) {
  const { t } = useTranslation();
  const ingest = useStreamIngest(uuid);
  const eps = ingest.data?.ingestEndpoints ?? [];

  return (
    <section className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
      <p className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Server className="h-4 w-4 text-muted-foreground" />
        {t("ingest.title")}
      </p>

      {ingest.isLoading ? (
        <Skeleton className="h-28 w-full rounded-lg" />
      ) : eps.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("ingest.empty")}</p>
      ) : (
        <div className="space-y-4">
          {ingest.data?.keyHidden && (
            <p className="rounded-md border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn">
              {t("ingest.keyHidden")}
            </p>
          )}
          {eps.map((ep) => (
            <div
              key={`${ep.nodeId}-${ep.protocol}`}
              className="space-y-2.5 rounded-lg border border-white/[0.07] bg-black/20 p-3"
            >
              <div className="text-xs font-medium text-foreground">
                {ep.nodeName}
                <span className="text-muted-foreground">
                  {" · "}
                  {ep.region} · {ep.protocol.toUpperCase()}
                </span>
              </div>
              <CopyField label={t("ingest.server")} value={ep.url} />
              {ep.rtmpStreamKeyField ? (
                <CopyField label={t("ingest.key")} value={ep.rtmpStreamKeyField} />
              ) : ep.streamId ? (
                <CopyField label={t("ingest.streamId")} value={ep.streamId} />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StudioBody({ stream, me }: { stream: StreamDetailDTO; me: MeDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const uuid = stream.uuid;
  const sock = useStreamSocket(uuid);
  const update = useUpdateStream();
  const end = useEndStream();
  const regen = useRegenerateKey();

  const canRecord = me.capabilities.canRecord ?? false;

  // Form state initialised once from the detail snapshot (body is keyed by uuid → fresh per stream).
  const [title, setTitle] = useState(stream.title);
  const [slug, setSlug] = useState(stream.slug ?? "");
  const [description, setDescription] = useState(stream.description ?? "");
  const [scheduledStartAt, setScheduledStartAt] = useState(toLocalInput(stream.scheduledStartAt));
  const [newKey, setNewKey] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Prefer the live socket status frame; fall back to the REST snapshot (mirrors StreamPage).
  const liveStatus = sock.status?.state ?? stream.status;
  const playbackEndpoints = sock.status?.playbackEndpoints ?? stream.playbackEndpoints;
  const scheduledForPlayer =
    sock.status?.scheduledStartAt != null
      ? new Date(sock.status.scheduledStartAt).toISOString()
      : stream.scheduledStartAt;
  const startedAtISO =
    sock.status?.startedAt != null ? new Date(sock.status.startedAt).toISOString() : stream.startedAt;
  const viewers = sock.viewers || stream.viewerCount;

  // Live uptime ticker.
  useEffect(() => {
    if (liveStatus !== "live") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [liveStatus]);

  const uptime = useMemo(() => {
    void now; // re-derive each tick
    return liveStatus === "live" ? formatUptime(startedAtISO) : "";
  }, [now, liveStatus, startedAtISO]);

  const save = async () => {
    const body: UpdateStreamBody = {
      title: title.trim(),
      slug: slug.trim() ? slug.trim() : null,
      description: description.trim() ? description.trim() : null,
      scheduledStartAt: scheduledStartAt ? new Date(scheduledStartAt).toISOString() : null,
    };
    try {
      await update.mutateAsync({ uuid, body });
      toast.success(t("manage.saved"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("manage.saveFailed"));
    }
  };

  const doEnd = async () => {
    try {
      await end.mutateAsync(uuid);
      toast.success(t("manage.ended"));
      navigate("/studio");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("manage.endFailed"));
    }
  };

  const doRegen = async () => {
    try {
      const res = await regen.mutateAsync(uuid);
      setNewKey(res.streamKey);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("manage.resetFailed"));
    }
  };

  const watchTo = `/watch/${stream.slug ?? uuid}`;
  const canEnd = liveStatus !== "ended";
  const recordingCount = stream.recordings?.length ?? 0;

  return (
    <>
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/studio">
            <ArrowLeft className="h-4 w-4" />
            {t("studioStream.back")}
          </Link>
        </Button>
        <div className="order-last flex min-w-0 basis-full items-center gap-2.5 sm:order-none sm:basis-0 sm:flex-1">
          <h1 className="truncate font-display text-xl font-bold tracking-tight text-foreground sm:text-2xl">
            {stream.title}
          </h1>
          {liveStatus === "live" ? (
            <LiveBadge />
          ) : liveStatus === "paused" ? (
            <PausedBadge />
          ) : (
            <StatusBadge status={liveStatus} />
          )}
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to={watchTo}>
            <ExternalLink className="h-4 w-4" />
            {t("studioStream.viewPublic")}
          </Link>
        </Button>
      </div>

      {/* Focus row: live preview (+ stats) | real-time chat */}
      <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
          <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-black shadow-panel">
            <Player
              status={liveStatus}
              playbackEndpoints={playbackEndpoints}
              scheduledStartAt={scheduledForPlayer}
              recordings={stream.recordings}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Stat
              label={t("manage.sViewers")}
              value={liveStatus === "live" ? formatCount(viewers) : "—"}
              icon={Eye}
            />
            <Stat
              label={t("manage.sUptime")}
              value={liveStatus === "live" && uptime ? uptime : "—"}
              icon={Clock}
              mono
            />
            <Stat
              label={t("manage.sStarted")}
              value={startedAtISO ? relativeTime(startedAtISO) : t("manage.notStarted")}
            />
            <Stat label={t("manage.sRecordings")} value={String(recordingCount)} icon={Video} />
          </div>
        </div>

        <div className="min-h-[60vh] lg:min-h-0">
          <ChatPanel
            messages={sock.messages}
            conn={sock.conn}
            sysNotice={sock.sysNotice}
            sendChat={sock.sendChat}
            fetchOlder={sock.fetchOlder}
          />
        </div>
      </div>

      {/* All info */}
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <IngestSection uuid={uuid} />

          {/* Edit details */}
          <section className="space-y-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{t("studioStream.detailsTitle")}</span>
              <Button
                variant="primary"
                size="sm"
                onClick={save}
                loading={update.isPending}
                disabled={!title.trim()}
              >
                {t("manage.save")}
              </Button>
            </div>
            <div>
              <Label htmlFor="ms-title">{t("manage.fieldTitle")}</Label>
              <Input id="ms-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ms-slug">{t("manage.slug")}</Label>
              <Input
                id="ms-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t("manage.slugPlaceholder")}
              />
            </div>
            <div>
              <Label htmlFor="ms-desc">{t("manage.description")}</Label>
              <textarea
                id="ms-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={textareaCls}
              />
            </div>
            <div>
              <Label htmlFor="ms-sched">{t("manage.schedule")}</Label>
              <Input
                id="ms-sched"
                type="datetime-local"
                value={scheduledStartAt}
                onChange={(e) => setScheduledStartAt(e.target.value)}
              />
            </div>
            {canRecord && (
              // Read-only: recording is fixed at create time (it sets the CF recording mode).
              <div className="flex items-center justify-between rounded-md border border-white/[0.07] bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground">
                {t("manage.record")}
                <span
                  className={`h-2 w-2 rounded-full ${stream.recordEnabled ? "bg-red-500" : "bg-muted-foreground/40"}`}
                  title={t("manage.record")}
                />
              </div>
            )}
          </section>
        </div>

        <div className="space-y-5">
          {/* Stream key */}
          <section className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
            <p className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              {t("manage.streamKey")}
            </p>
            {newKey ? (
              <div className="space-y-2">
                <p className="text-xs text-warn">{t("manage.keyOnce")}</p>
                <CopyField value={newKey} />
              </div>
            ) : (
              <div className="space-y-2.5">
                <p className="text-xs text-muted-foreground">{t("manage.resetHint")}</p>
                <Button variant="outline" size="sm" onClick={doRegen} loading={regen.isPending}>
                  {t("manage.resetKey")}
                </Button>
              </div>
            )}
          </section>

          {/* Recordings */}
          {recordingCount > 0 && (
            <section className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
              <p className="mb-2 text-sm font-medium text-foreground">
                {t("manage.recordings", { count: recordingCount })}
              </p>
              <Button asChild variant="secondary" size="sm">
                <a href={api.url(`/api/streams/${uuid}/recording/download`)}>
                  <Download className="h-4 w-4" />
                  {t("manage.downloadLatest")}
                </a>
              </Button>
            </section>
          )}

          {/* Danger zone */}
          {canEnd && (
            <section className="rounded-xl border border-destructive/30 bg-destructive/[0.06] p-4">
              <p className="mb-2 text-sm font-medium text-foreground">{t("studioStream.dangerTitle")}</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive-soft hover:bg-destructive/15"
                  >
                    <Square className="h-4 w-4" />
                    {t("manage.endStream")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("manage.endConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("manage.endConfirmDesc")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("manage.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={doEnd}>{t("manage.endStream")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

export function StudioStreamPage() {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { data: me, isLoading: meLoading } = useMe();
  const { openLogin } = useLoginModal();
  const detail = useStream(uuid);

  if (meLoading || detail.isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Skeleton className="aspect-video w-full rounded-2xl" />
          <Skeleton className="h-[60vh] w-full rounded-2xl lg:h-auto" />
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("studio.loginTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("studio.loginHint")}</p>
        <Button variant="primary" className="mt-4" onClick={openLogin}>
          {t("nav.login")}
        </Button>
      </div>
    );
  }

  if (!hasPublish(me.capabilities)) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("studio.noPublishTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("studio.noPublishHint")}</p>
      </div>
    );
  }

  if (detail.isError || !detail.data || !detail.data.isOwner) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("studioStream.notFound")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("studioStream.notFoundHint")}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/studio">{t("studioStream.back")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="enter"
      className="mx-auto max-w-7xl px-4 py-6"
    >
      <StudioBody key={detail.data.uuid} stream={detail.data} me={me} />
    </motion.main>
  );
}
